<script setup>
import { computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  data: { type: Object, default: () => ({ actions: [], contextSummary: null, confidence: 0, generatedAt: null }) },
  loading: { type: Boolean, default: false },
  running: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'run']);

const URGENCY_META = {
  agora: { label: 'AGORA', bg: 'bg-rose-600', text: 'text-white' },
  '24h': { label: 'em 24h', bg: 'bg-amber-500', text: 'text-white' },
  '72h': { label: 'em 72h', bg: 'bg-blue-500', text: 'text-white' },
  semana: { label: 'esta semana', bg: 'bg-slate-500', text: 'text-white' },
};

const CATEGORY_META = {
  resposta: { label: 'Resposta', icon: 'i-lucide-message-square-reply' },
  conteudo: { label: 'Conteúdo', icon: 'i-lucide-pencil' },
  crise: { label: 'Crise', icon: 'i-lucide-shield-alert' },
  posicionamento: { label: 'Posicionamento', icon: 'i-lucide-target' },
  mobilizacao: { label: 'Mobilização', icon: 'i-lucide-megaphone' },
};

const IMPACT_COLOR = {
  alto: 'bg-emerald-100 text-emerald-700',
  medio: 'bg-amber-100 text-amber-700',
  baixo: 'bg-slate-100 text-slate-600',
};

const EFFORT_COLOR = {
  rapido: 'bg-emerald-50 text-emerald-700',
  medio: 'bg-amber-50 text-amber-700',
  longo: 'bg-rose-50 text-rose-700',
};

const formatDate = d => (d ? new Date(d).toLocaleString('pt-BR') : '');

const groupedByUrgency = computed(() => {
  const order = ['agora', '24h', '72h', 'semana'];
  const groups = {};
  (props.data.actions || []).forEach(a => {
    groups[a.urgency] ||= [];
    groups[a.urgency].push(a);
  });
  return order.filter(u => groups[u]?.length).map(u => ({ urgency: u, items: groups[u] }));
});
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="fixed inset-0 z-50 flex" @click.self="emit('close')">
      <div class="flex-1 bg-black/50 backdrop-blur-sm" @click="emit('close')" />
      <aside class="h-full w-full max-w-2xl overflow-y-auto bg-n-background shadow-2xl">
        <!-- Header -->
        <div class="sticky top-0 z-10 border-b border-n-strong/40 bg-gradient-to-r from-blue-600 to-indigo-700 p-5 text-white">
          <div class="flex items-start justify-between gap-3">
            <div class="flex items-start gap-3">
              <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white/15">
                <span class="i-lucide-sparkles size-5" />
              </div>
              <div class="min-w-0 flex-1">
                <h2 class="text-base font-bold">Copiloto de Campanha</h2>
                <p class="text-[11px] opacity-90">
                  Diante de tudo, o que fazer agora?
                </p>
              </div>
            </div>
            <div class="flex shrink-0 items-center gap-2">
              <Button
                variant="solid"
                color="slate"
                size="sm"
                icon="i-lucide-refresh-cw"
                :is-loading="running"
                :disabled="disabled"
                class="!bg-white/15 !text-white hover:!bg-white/25"
                @click="emit('run')"
              >
                Atualizar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                icon="i-lucide-x"
                class="!text-white hover:!bg-white/15"
                @click="emit('close')"
              />
            </div>
          </div>

          <div v-if="data.contextSummary" class="mt-3 rounded-lg bg-white/10 p-3 text-[12px] leading-relaxed">
            {{ data.contextSummary }}
          </div>
          <div v-if="data.generatedAt" class="mt-2 flex items-center gap-3 text-[10px] opacity-90">
            <span>Gerado em {{ formatDate(data.generatedAt) }}</span>
            <span v-if="data.confidence">Confiança: {{ data.confidence }}%</span>
          </div>
        </div>

        <!-- Body -->
        <div class="p-5">
          <div v-if="disabled" class="py-10 text-center text-sm text-n-slate-11">
            Selecione um perfil para ativar o Copiloto.
          </div>

          <div v-else-if="loading || running" class="flex flex-col items-center gap-2 py-10">
            <Spinner />
            <p class="text-xs text-n-slate-11">
              {{ running ? 'Gerando recomendações…' : 'Carregando…' }}
            </p>
          </div>

          <div
            v-else-if="!data.actions?.length"
            class="rounded-lg border border-dashed border-n-weak p-8 text-center text-sm text-n-slate-11"
          >
            <span class="i-lucide-sparkles mx-auto mb-2 block size-8 text-n-slate-11" />
            Clique em "Atualizar" pra gerar a primeira lista de ações priorizadas.
          </div>

          <div v-else class="space-y-5">
            <div v-for="g in groupedByUrgency" :key="g.urgency">
              <div class="mb-2 flex items-center gap-2">
                <span :class="[URGENCY_META[g.urgency]?.bg, URGENCY_META[g.urgency]?.text, 'rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide']">
                  {{ URGENCY_META[g.urgency]?.label }}
                </span>
                <span class="text-[11px] text-n-slate-11">{{ g.items.length }} ação(ões)</span>
              </div>

              <div class="space-y-2">
                <div
                  v-for="a in g.items"
                  :key="a.title"
                  class="rounded-lg border border-n-strong/30 bg-n-background p-3 shadow-sm"
                >
                  <div class="mb-2 flex items-start gap-2">
                    <span class="flex size-7 shrink-0 items-center justify-center rounded-md bg-blue-100 text-[11px] font-bold text-blue-700">
                      P{{ a.priority }}
                    </span>
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-bold text-n-slate-12">{{ a.title }}</p>
                      <p class="mt-0.5 text-xs text-n-slate-11">{{ a.description }}</p>
                    </div>
                  </div>

                  <div class="flex flex-wrap items-center gap-1.5 text-[10px]">
                    <span class="inline-flex items-center gap-1 rounded bg-n-slate-3 px-1.5 py-0.5 font-medium text-n-slate-12">
                      <span :class="CATEGORY_META[a.category]?.icon" class="size-3" />
                      {{ CATEGORY_META[a.category]?.label }}
                    </span>
                    <span :class="[IMPACT_COLOR[a.expectedImpact], 'rounded px-1.5 py-0.5 font-medium']">
                      Impacto {{ a.expectedImpact }}
                    </span>
                    <span :class="[EFFORT_COLOR[a.effort], 'rounded px-1.5 py-0.5 font-medium']">
                      Esforço {{ a.effort }}
                    </span>
                  </div>

                  <div v-if="a.evidence?.length" class="mt-2 border-t border-n-weak pt-2">
                    <p class="mb-1 text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">
                      Baseado em
                    </p>
                    <ul class="space-y-0.5">
                      <li
                        v-for="(ev, i) in a.evidence"
                        :key="i"
                        class="text-[11px] text-n-slate-11"
                      >
                        • {{ ev }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </Teleport>
</template>
